'use client';

import { SectionCard, SectionParagraph } from './section-card';
import { SectionGrid } from './section-grid';
import type { DefinitionPersona } from '@/hooks/use-definition';

interface PersonaTemplateProps {
  persona: DefinitionPersona;
}

export function PersonaTemplate({ persona }: PersonaTemplateProps) {
  const hasGoalsOrChallenges = persona.primaryGoals || persona.challenges;
  const hasToolsOrMotivations = persona.tools || persona.motivations;

  return (
    <div className="space-y-6">
      {/* Overview / Demographics */}
      {persona.demographics && (
        <SectionCard title="Overview">
          <SectionParagraph text={persona.demographics} />
        </SectionCard>
      )}

      {/* Goals & Challenges side by side */}
      {hasGoalsOrChallenges && (
        <SectionGrid>
          {persona.primaryGoals && (
            <SectionCard title="Goals & Objectives" dotColor="bg-green-500" titleColor="text-green-700" borderColor="border-green-200">
              <SectionParagraph text={persona.primaryGoals} />
            </SectionCard>
          )}
          {persona.challenges && (
            <SectionCard title="Frustrations & Pain Points" dotColor="bg-red-500" titleColor="text-red-600" borderColor="border-red-200">
              <SectionParagraph text={persona.challenges} />
            </SectionCard>
          )}
        </SectionGrid>
      )}

      {/* Background / Contexts */}
      {persona.contexts && (
        <SectionCard title="Background & Context">
          <SectionParagraph text={persona.contexts} />
        </SectionCard>
      )}

      {/* Tools & Motivations */}
      {hasToolsOrMotivations && (
        <SectionGrid>
          {persona.tools && (
            <SectionCard title="Key Behaviors & Tools">
              <SectionParagraph text={persona.tools} />
            </SectionCard>
          )}
          {persona.motivations && (
            <SectionCard title="Motivations">
              <SectionParagraph text={persona.motivations} />
            </SectionCard>
          )}
        </SectionGrid>
      )}

      {/* Accessibility */}
      {persona.accessibilityNeeds && (
        <SectionCard title="Accessibility Needs">
          <SectionParagraph text={persona.accessibilityNeeds} />
        </SectionCard>
      )}

      {/* Quotes */}
      {persona.quotes && (
        <SectionCard title="Notable Quotes">
          <div className="space-y-3">
            {persona.quotes.split('\n').filter(Boolean).map((quote, i) => (
              <blockquote key={i} className="border-l-4 border-brand-primary/30 pl-4 py-1 text-sm text-gray-600 italic">
                {quote.replace(/^[""]|[""]$/g, '').trim()}
              </blockquote>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}
